import { Image, Flex, Text, Box } from 'rebass';
import { Formik } from 'formik';
import { connect, useDispatch } from 'react-redux';
import { string, object } from 'yup';
import PropTypes from 'prop-types';
import React, { useEffect, useContext, useState } from 'react';
import difference from 'lodash/difference';
import pick from 'lodash/pick';
import posed from 'react-pose';
import { FaPlus } from "react-icons/fa6";
import { ReactComponent as BabysFYLabel } from 'babys-first-year/assets/babyyearbookslabel.svg';
import store from 'store';

import ButtonBar from '../../story-pages/button-bar';
import { bodyWithLockVersion, getJSDateFromISO } from '../../app/form/helper';
import { destroyStory, updateStory } from '../actions';
import { useEntity } from '../../entities/hooks';
import { useMatch } from '../../app/hooks';
import { useHistory } from 'react-router-dom';

import Main from './main';
import MoveStoryPageProvider from '../edit-story/move';
import NewStoryHeader from './header';
import StoryPageList from '../../story-pages/list';
import { useFirstTimeUserFlowVisible, useStoryWithJournalId } from '../hooks/index';
import { CurrentCollectionContext } from '../../collections/current-collection-context';
import { useRouteFromEntityParams } from '../../collections/hooks/index';
import NewStoryFooter from './footer';
import useLocation from 'app/hooks/location';
import { LetsBeginFooterComponent } from './global/lets-begin';
import Modal from 'app/modal/v3/index';
import newStory from './assets/newstory.png'
import { Button } from 'story-pages/button-bar/styled';
import { setSidebar } from 'ui/actions';
import Loading from 'app/loading/index';

const ListWrap = posed.div({
  loading: { opacity: 0.25 },
});

const NewStoryPages = props => {
  const {
    params: { id },
  } = useMatch();
  const { pathname } = useLocation();
  const isFirstTimeUser = useFirstTimeUserFlowVisible()
  const [firstTimeUserModal, setFirstTimeUserModal] = useState(false)
  const dispatch = useDispatch()
  const isFromTellAStory = pathname.includes('/tell_a_story/')
  const ftui_watched = store.get('woohoo_ftui_watched');

  useEffect(() => {
    if (!ftui_watched || pathname.includes('/tell_a_story/'))
      dispatch(setSidebar(false))

    setFirstTimeUserModal(!ftui_watched || pathname.includes('/tell_a_story/'))
  }, [ftui_watched])

  const { replace, push } = useHistory();
  const { entity } = useEntity({ id, type: 'stories' });
  const story = useStoryWithJournalId(entity);
  const { currentCollection, collectionsFilter } = useContext(CurrentCollectionContext);

  const journalId = currentCollection.id && currentCollection.id !== 'undefined' ? currentCollection.id : (story?.['journal-ids']?.[0] || '');
  const backToCollectionRoute = useRouteFromEntityParams({
    type: currentCollection.type,
    id: journalId,
  });

  const pushBack = () => {
    if (pathname.includes('/tell_a_story/') && story?.['child-journal-story-request']?.id) {
      return push(`/tell_a_story/${story?.['child-journal-story-request']?.id}`);
    }
    if (journalId && backToCollectionRoute) {
      return push(backToCollectionRoute);
    }
    if (collectionsFilter) {
      return push(`/${collectionsFilter}`);
    }

    return push(`/child-journals/${journalId}`);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const onCancel = (setSubmitting) => {
    setSubmitting(true);
    props.destroyStory(id, journalId).then(() => {
      // trigger('/v3/my/chronicle_collection');
      pushBack();
    });
  }

  const onClose = () => {
    store.set('woohoo_ftui_watched', true);
    setFirstTimeUserModal(false)
  }

  return (<>
    {firstTimeUserModal && <Modal
      close={{
        onClose
      }}
      maxWidth={620}
      closeOnOutsideClick={false}
      isScrollActive={false}
      aboveTooltip
      shadeProps={{ pt: [0, 4] }}
    >
      <Flex m="auto" mb="50px" flexDirection="column" justifyContent="center" alignItems="center" maxWidth={400}>
        <Text fontFamily="Bitter" fontWeight="normal"
          sx={{
            color: '#000',
            fontFamily: 'Bitter',
            fontSize: '30px',
            fontWeight: '500',
            lineHeight: '165%',
          }}
          px="26px"
        >
          {isFromTellAStory ? 'Woohoo, you’ve created your first story section!' : 'Woohoo, you’ve started your first story!'}
        </Text>
        <Image
          maxWidth={382}
          src={newStory}
        />
        <Text
          p={2}
          color="#64748B"
          fontFamily="Poppins"
          fontSize="18px"
          fontStyle="normal"
          fontWeight={400}
          lineHeight="200%"
          letterSpacing="-0.36px"
          textAlign='center'
          px="26px"
        >
          {isFromTellAStory ? <>Continue to <b>add story sections</b> with the plus icon</> : 'To continue this story, add more content above or below by tapping the + button. It will look like this:'}
        </Text>
        <Flex justifyContent="space-around" alignItems="center">
          <Box width="120px" sx={{
            height: '1.5px',
            backgroundColor: '#64748B',
            opacity: '0.3',
          }} />
          <Button sx={{ cursor: 'default' }}>
            <Flex justifyContent="center" alignItems="center">
              <FaPlus size="20px" />
            </Flex>
          </Button>
          <Box width="120px" sx={{
            height: '1.5px',
            backgroundColor: '#64748B',
            opacity: '0.3',
          }} />
        </Flex>
        {isFromTellAStory && <Text
          p={2}
          color="#64748B"
          fontFamily="Poppins"
          fontSize="18px"
          fontStyle="normal"
          fontWeight={400}
          lineHeight="200%"
          letterSpacing="-0.36px"
          textAlign='center'
          px="26px"
        >
          Then click <b>Next</b> when you’re done.
        </Text>}
        <LetsBeginFooterComponent
          sx={{ justifyContent: 'center', }}
          onStepUp={onClose}
          nextButtonText="Okay"
        />
      </Flex>
    </Modal>}
    <Formik
      initialValues={{
        'began-on': (story['began-on'] && getJSDateFromISO(story['began-on'])) || new Date(),
        'ended-on': story['ended-on'] ? getJSDateFromISO(story['ended-on']) : '',
        'journal-id': story['journal-id'],
      }}
      validationSchema={object().shape({
        'began-on': string(),
        'ended-on': string(),
      })}
      onSubmit={async values => {
        const isDirty = difference(
          Object.values(pick(story, ['began-on', 'ended-on'])),
          Object.values(pick(values, ['began-on', 'ended-on']))
        );
        if (isDirty) {
          delete values?.['began-on'];
          await props.updateStory(id, bodyWithLockVersion(story, values));
        }

        push(`/${pathname?.includes('tell_a_story') ? 'tell_a_story' : 'stories'}/new/${id}/cover`);
      }}
    >
      {({ isSubmitting, setSubmitting, handleSubmit, values }) => {
        if (isSubmitting)
          return <Loading showText />

        return (
          <Box sx={{ zIndex: 1, bg: 'white', position: 'relative', height: "110vh" }}>
            {pathname?.includes('tell_a_story') && <Flex width="100%" p={2} bg="white" justifyContent={'space-between'} alignItems="center" >
              <Flex width={['160px', '300px', "100%"]}>
                <BabysFYLabel />
              </Flex>
            </Flex>}
            <NewStoryHeader
              disabled={isSubmitting}
              onCancel={() => onCancel(setSubmitting)}
              onNext={handleSubmit}
              title="New Story"
            />
            <Main mb={['100px', 0]}>
              <MoveStoryPageProvider>
                <ListWrap pose={isSubmitting ? 'loading' : ''}>
                  <ButtonBar storyId={id} position={0} />
                  <StoryPageList storyId={id} editing />
                </ListWrap>
              </MoveStoryPageProvider>
            </Main>
            <NewStoryFooter
              disabled={isSubmitting}
              onCancel={() => onCancel(setSubmitting)}
              onNext={handleSubmit}
            />
          </Box>
        )
      }}
    </Formik>
  </>
  );
};

NewStoryPages.propTypes = {
  destroyStory: PropTypes.func.isRequired,
  updateStory: PropTypes.func.isRequired,
};

export default connect(null, { destroyStory, updateStory })(NewStoryPages);
